import { baseApiUrl } from 'src/config';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';

/**
 * BRK-2036: Document Revision History Audit (epic BRK-2028). Typed fetch against
 * document-service's generate endpoint (BRK-2034) — no redux saga/reducer/action ceremony,
 * this is a one-shot command whose only result is a URL the caller navigates to.
 * Same shape as mdsWorksheet.ts, and the types mirror the document-service DTOs.
 */

/**
 * The three per-row flags provider-service adds to the completed-documents list response
 * (BRK-2035). All optional: a list that predates the feature, or one served while the Split
 * treatment is off, omits them entirely and every predicate below must read that as "no".
 */
export type RevisionAuditFlags = {
  /** Trail-based: any RESUBMIT/COSIGNER_EDIT/ICD10_EDIT row exists after the first sign row. */
  revisionAuditEligible?: boolean;
  /** Presigned URL of the newest stored export, or null/absent when none has been generated. */
  revisionAuditPdfUrl?: string | null;
  /** The stored export was generated against an older trail leaf — a newer one is available. */
  revisionAuditStale?: boolean;
};

export type RevisionAuditResult = { pdfUrl: string };

const headers = async () => ({
  Authorization: `Bearer ${await getAccessToken()}`,
  'x-tenant-id': getRealmName() || '',
});

const parseEnvelope = async (response: Response, fallback: string) => {
  let body: any = null;
  try {
    body = await response.json();
  } catch (e) {
    throw new Error(`${fallback} (HTTP ${response.status})`);
  }
  if (body?.code !== 'OK') {
    throw new Error(body?.message || fallback);
  }
  return body.data;
};

/**
 * Generate (or regenerate) the revision-appendix PDF for one completed document and return a
 * presigned URL to it. Synchronous on the server, ~3-6s: it walks the audit trail, diffs the
 * JSON snapshots, renders the appendix and merges it onto the signed PDF byte-for-byte.
 *
 * Re-gates everything the menu gated — Split treatment, formType allowlist, signed state,
 * eligibility — and 400s when the diff comes back empty (a zero-change resubmit can leave a
 * document flagged eligible with nothing to report), so callers must surface `message`.
 */
export const generateRevisionAuditPdf = async (
  formSubmissionUuid: string
): Promise<RevisionAuditResult> => {
  const response = await fetch(
    `${baseApiUrl}/document/revision-audit/${formSubmissionUuid}/generate`,
    { method: 'POST', headers: await headers() }
  );
  return parseEnvelope(response, 'Failed to generate the document with revisions');
};
